// Subject API service — fetches board-level and school-level subjects

import { apiRequest } from './api';

// ── Types ───────────────────────────────────────────────────

export type SubjectApiRecord = {
    id: string;
    subject_id: number;
    name: string;
    code: string;
    is_core_subject: boolean;
    is_active: boolean;
    created_at: string;
    updated_at: string;
};

export type OrgSubjectRecord = {
    id: string;
    organization_id: string;
    is_active: boolean;
    created_at: string;
    updated_at: string;
    subject?: {
        id: number;
        name: string;
        code: string;
        type: string;
        has_practical: boolean;
        max_marks: number;
        board_id: number;
    };
};

export type SchoolSubjectRecord = {
    id: string;
    organization_id: string;
    school_id: string;
    organization_subject_id: string;
    is_active: boolean;
    created_at: string;
    updated_at: string;
    organization_subject?: {
        id: string;
        is_active: boolean;
        subject?: {
            id: number;
            name: string;
            code: string;
            type: string;
            has_practical: boolean;
            max_marks: number;
            board_id: number;
        };
    };
};

export type SubjectListResponse = {
    success: boolean;
    message: string;
    data: SubjectApiRecord[];
    meta: {
        current_page: number;
        last_page: number;
        per_page: number;
        total: number;
    };
};

// ── API Functions ───────────────────────────────────────────

/**
 * Fetch subjects for a given board.
 */
export async function fetchBoardSubjects(boardId: number, params?: {
    page?: number;
    perPage?: number;
    search?: string;
}): Promise<SubjectListResponse> {
    const query = new URLSearchParams();
    if (params?.page) query.set('page', String(params.page));
    if (params?.perPage) query.set('per_page', String(params.perPage));
    if (params?.search) query.set('search', params.search);

    const qs = query.toString();
    const res = await apiRequest<SubjectApiRecord[]>(
        `/api/boards/${boardId}/subjects${qs ? `?${qs}` : ''}`
    );

    return {
        success: res.success,
        message: res.message,
        data: res.data,
        meta: (res as unknown as SubjectListResponse).meta,
    };
}

/**
 * Fetch subjects available for the current school's organization.
 */
export async function fetchOrgSubjects(params?: {
    perPage?: number;
}): Promise<OrgSubjectRecord[]> {
    const query = new URLSearchParams();
    if (params?.perPage) query.set('per_page', String(params.perPage));
    const qs = query.toString();
    const res = await apiRequest<OrgSubjectRecord[]>(
        `/api/organization-subjects${qs ? `?${qs}` : ''}`
    );
    return res.data;
}

/**
 * Fetch subjects enabled for a specific school.
 */
export async function fetchSchoolSubjects(schoolId: string, params?: {
    perPage?: number;
    isActive?: boolean;
}): Promise<SchoolSubjectRecord[]> {
    const query = new URLSearchParams();
    if (params?.perPage) query.set('per_page', String(params.perPage));
    if (params?.isActive !== undefined) query.set('is_active', params.isActive ? '1' : '0');
    const qs = query.toString();
    const res = await apiRequest<SchoolSubjectRecord[]>(
        `/api/schools/${schoolId}/subjects${qs ? `?${qs}` : ''}`
    );
    return res.data;
}

/**
 * Add a board subject to the organization.
 */
export async function addOrgSubject(boardSubjectId: string): Promise<OrgSubjectRecord> {
    const res = await apiRequest<OrgSubjectRecord>(
        '/api/organization-subjects',
        {
            method: 'POST',
            body: JSON.stringify({ board_subject_id: Number(boardSubjectId) }),
        }
    );
    return res.data;
}

/**
 * Enable an organization subject for a school.
 */
export async function enableSchoolSubject(
    schoolId: string,
    organizationSubjectId: string,
    isActive = true
): Promise<SchoolSubjectRecord> {
    const res = await apiRequest<SchoolSubjectRecord>(
        `/api/schools/${schoolId}/subjects`,
        {
            method: 'POST',
            body: JSON.stringify({
                organization_subject_id: organizationSubjectId,
                is_active: isActive,
            }),
        }
    );
    return res.data;
}

export async function removeOrgSubject(subjectId: string) {
    const result = await apiRequest(`/api/organization-subjects/${subjectId}`, {
        method: 'DELETE'
    });
    return result.data;
}

export async function disableSchoolSubject(details: {
    schoolId: string,
    schoolSubjectId: string
}) {
    const result = await apiRequest(`/api/schools/${details.schoolId}/subjects/${details.schoolSubjectId}`, {
        method: 'DELETE'
    });
    return result.data;
}